import { orcamentoSchema, type FreteOrcamentoDto, type OrcamentoDto, type OrcamentoItemMc, type OrcamentoItemResponse, type OrcamentoResponse } from '@triade/contracts';
import { decimalDigitado, formatarDecimal } from '@/lib/format';

/**
 * Estado do formulario do orcamento e conversoes de/para o contrato. Texto digitado fica como o usuario escreveu
 * ("25,00"); so vira string decimal da API em `paraDto`. Nada aqui calcula preco: totais, lucro e faixa vem da API.
 */

export interface ClienteResumo {
  id: string;
  nome: string;
  documento?: string | null;
  /** UF do endereco principal do cliente (espelho do cadastro). Preenche a UF de destino. */
  uf?: string | null;
}

export interface LinhaForm {
  /** Chave estavel da linha na grade (nao e o id do item: o mesmo item pode entrar duas vezes). */
  chave: string;
  itemId: string;
  codigo: string;
  descricao: string;
  unidade: string;
  quantidade: string;
  /** Preco negociado (com IPI, a prazo), texto digitado. */
  preco: string;
}

/** 'ANTIGA' = forma antiga (receita/custo em R$ do orcamento); CIF/FOB = frete detalhado (modalidade). */
export type ModoFrete = 'ANTIGA' | 'CIF' | 'FOB';
/** CIF: o frete e informado como valor total ou como R$ por t (exclusivos). */
export type BaseFrete = 'TOTAL' | 'POR_T';

export interface FormOrcamento {
  data: string;
  /** Data prevista de faturamento (fato gerador da reforma). '' = a data do orcamento. */
  dataFaturamento: string;
  tabelaId: string;
  condicaoId: string;
  cliente: ClienteResumo | null;
  /** UF de destino (ICMS/IBS). '' = sem UF: nao vai no corpo. */
  ufDestino: string;
  /** true = o usuario escolheu/digitou a UF; trocar o cliente nao sobrescreve. */
  ufDestinoManual: boolean;
  descricao: string;
  freteReceita: string;
  freteCusto: string;
  consideraFreteLucro: boolean;
  freteModo: ModoFrete;
  freteTransportadora: string;
  freteBase: BaseFrete;
  freteTotal: string;
  fretePorT: string;
  /** A transportadora gera credito de ICMS/PIS/COFINS (padrao marcado). */
  freteCreditaImpostos: boolean;
  /** Frete cobrado do cliente em linha propria (so CIF). */
  freteDestacado: boolean;
  /** "Valor cobrado do cliente" (receita do frete destacado). '' = o frete total. */
  freteCobrado: string;
  linhas: LinhaForm[];
}

export function formVazio(hoje: string): FormOrcamento {
  return {
    data: hoje,
    dataFaturamento: '',
    tabelaId: '',
    condicaoId: '',
    cliente: null,
    ufDestino: '',
    ufDestinoManual: false,
    descricao: '',
    freteReceita: '',
    freteCusto: '',
    consideraFreteLucro: false,
    freteModo: 'ANTIGA',
    freteTransportadora: '',
    freteBase: 'TOTAL',
    freteTotal: '',
    fretePorT: '',
    freteCreditaImpostos: true,
    freteDestacado: false,
    freteCobrado: '',
    linhas: [],
  };
}

let contador = 0;
export function novaChave(): string {
  contador += 1;
  return `l${contador}`;
}

interface SnapshotLido {
  table?: { id?: string };
  refs?: { tabelaId?: string };
  freight?: { revenue?: string; cost?: string };
  items?: Array<{ seq: number; unitPrice: string; quantity: string }>;
}

/** Frete detalhado gravado -> campos do formulario. Custo (total, R$/t, credito) so vem com custo.ver: sem ele ficam vazios. */
function freteDeDetalhe(o: OrcamentoResponse): Pick<FormOrcamento, 'freteModo' | 'freteTransportadora' | 'freteBase' | 'freteTotal' | 'fretePorT' | 'freteCreditaImpostos' | 'freteDestacado' | 'freteCobrado'> {
  const fd = o.exibicao?.freteDetalhe;
  const receita = texto(o.freteReceita);
  const total = texto(fd?.total);
  if (!fd) {
    return { freteModo: 'ANTIGA', freteTransportadora: '', freteBase: 'TOTAL', freteTotal: '', fretePorT: '', freteCreditaImpostos: true, freteDestacado: false, freteCobrado: '' };
  }
  // A receita do frete destacado e o valor cobrado informado ou, se vazio, o frete total: so preenche "valor cobrado" quando difere do total.
  const cobrado = fd.destacado && receita !== '' && receita !== total ? receita : '';
  return {
    freteModo: fd.modalidade,
    freteTransportadora: fd.transportadora ?? '',
    freteBase: fd.porT ? 'POR_T' : 'TOTAL',
    freteTotal: total,
    fretePorT: texto(fd.porT ?? null),
    freteCreditaImpostos: fd.transportadoraCredita ?? true,
    freteDestacado: fd.destacado,
    freteCobrado: cobrado,
  };
}

/** "2.500000" -> "2,5"; "20" -> "20". So reescreve os caracteres (sem agrupar milhar, para nao confundir o digitar). */
export function quantidadeTexto(q: string): string {
  const semZeros = q.includes('.') ? q.replace(/0+$/, '').replace(/\.$/, '') : q;
  return semZeros.replace('.', ',');
}

/** "50.000000" -> "50,00"; "12.3456" -> "12,3456". Tira so zeros a direita (sem arredondar) e mantem 2 casas. */
export function decimalTexto(v: string): string {
  const aparado = v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v;
  return formatarDecimal(aparado, 2);
}

/** Chave da linha da grade a partir do seq do item gravado (estavel entre recargas). */
export const chaveDoItem = (seq: number): string => `s${seq}`;

/** MC por linha (chave da grade) dos itens gravados; vazio se a API nao mandou `mc` (versao antiga/importada). */
export function mcPorChave(itens: readonly OrcamentoItemResponse[] | undefined): Map<string, OrcamentoItemMc> {
  return new Map((itens ?? []).flatMap((i) => (i.mc ? [[chaveDoItem(i.seq), i.mc] as const] : [])));
}

const texto = (v: string | null | undefined): string => (v === null || v === undefined ? '' : decimalTexto(v));

/** Orcamento gravado -> formulario. Usa o snapshot (entrada exata do motor), nao os campos arredondados. */
export function deResposta(o: OrcamentoResponse): FormOrcamento {
  const snap = (o.snapshot ?? {}) as SnapshotLido;
  const entrada = new Map((snap.items ?? []).map((i) => [i.seq, i]));
  const exib = new Map((o.exibicao?.itens ?? []).map((i) => [i.seq, i]));
  return {
    data: o.data,
    dataFaturamento: o.dataFaturamento ?? '',
    tabelaId: snap.refs?.tabelaId ?? snap.table?.id ?? o.tabelaId,
    condicaoId: o.condicaoId,
    cliente: o.cliente ? { id: o.cliente.id, nome: o.cliente.nome, documento: o.cliente.documento } : null,
    // UF gravada na versao (resposta da API). Origem INFORMADA = o usuario escolheu; ENDERECO_PRINCIPAL = veio do cliente.
    ufDestino: o.ufDestino ?? '',
    ufDestinoManual: o.ufDestinoOrigem === 'INFORMADA',
    descricao: o.descricao ?? '',
    freteReceita: texto(o.freteReceita),
    freteCusto: texto(o.freteCusto ?? null),
    consideraFreteLucro: o.consideraFreteLucro,
    ...freteDeDetalhe(o),
    linhas: o.itens.map((i) => ({
      chave: chaveDoItem(i.seq),
      itemId: i.itemId,
      codigo: exib.get(i.seq)?.codigo ?? '',
      descricao: i.descricao,
      unidade: i.unidade,
      quantidade: quantidadeTexto(entrada.get(i.seq)?.quantity ?? i.quantity),
      preco: decimalTexto(entrada.get(i.seq)?.unitPrice ?? i.unitPrice),
    })),
  };
}

export interface ValidacaoForm {
  dto: OrcamentoDto | null;
  /** Mensagem do primeiro problema (para o usuario), quando `dto` e nulo. */
  problema: string | null;
}

/**
 * Bloco de frete -> `frete` do corpo. Forma antiga: receita/custo. CIF/FOB: `modalidade` (nunca `custo` junto).
 * Sem custo.ver, os campos de custo (total, R$/t, transportadora, credito) nao entram: a API preserva o gravado.
 */
function freteParaDto(f: FormOrcamento, comCusto: boolean): { frete?: FreteOrcamentoDto; problema?: string } {
  if (f.freteModo === 'ANTIGA') {
    const fr = f.freteReceita.trim() === '' ? undefined : decimalDigitado(f.freteReceita);
    const fc = f.freteCusto.trim() === '' ? undefined : decimalDigitado(f.freteCusto);
    if (fr === null) return { problema: 'Receita de frete inválida.' };
    if (fc === null) return { problema: 'Custo de frete inválido.' };
    if (fr === undefined && fc === undefined) return {};
    return { frete: { ...(fr !== undefined ? { receita: fr } : {}), ...(fc !== undefined ? { custo: fc } : {}) } };
  }
  const frete: FreteOrcamentoDto = { modalidade: f.freteModo };
  const transportadora = f.freteTransportadora.trim();
  if (comCusto && transportadora !== '') frete.transportadora = transportadora;
  if (f.freteModo === 'CIF') {
    frete.destacado = f.freteDestacado;
    if (f.freteDestacado && f.freteCobrado.trim() !== '') {
      const r = decimalDigitado(f.freteCobrado);
      if (r === null) return { problema: 'Valor cobrado do cliente inválido.' };
      frete.receita = r;
    }
    if (comCusto) {
      frete.transportadoraCredita = f.freteCreditaImpostos;
      const campo = f.freteBase === 'TOTAL' ? f.freteTotal : f.fretePorT;
      if (campo.trim() !== '') {
        const v = decimalDigitado(campo);
        if (v === null) return { problema: 'Valor do frete inválido.' };
        if (f.freteBase === 'TOTAL') frete.total = v;
        else frete.porT = v;
      }
    }
  }
  return { frete };
}

/** Formulario -> corpo do contrato, validado pelo mesmo schema Zod da API. `comCusto` = tem custo.ver. */
export function paraDto(f: FormOrcamento, comCusto = true): ValidacaoForm {
  if (!f.tabelaId) return { dto: null, problema: 'Escolha a tabela de preço.' };
  if (!f.condicaoId) return { dto: null, problema: 'Escolha a condição de pagamento.' };
  if (!f.data) return { dto: null, problema: 'Informe a data.' };
  if (f.linhas.length === 0) return { dto: null, problema: 'Adicione ao menos um item.' };
  const itens = [];
  for (const l of f.linhas) {
    const q = decimalDigitado(l.quantidade);
    const p = decimalDigitado(l.preco);
    if (q === null) return { dto: null, problema: `Quantidade inválida no item ${l.codigo}.` };
    if (p === null) return { dto: null, problema: `Preço inválido no item ${l.codigo}.` };
    itens.push({ itemId: l.itemId, quantidade: q, precoUnitario: p });
  }
  const fre = freteParaDto(f, comCusto);
  if (fre.problema) return { dto: null, problema: fre.problema };
  const candidato = {
    data: f.data,
    ...(f.dataFaturamento !== '' && f.dataFaturamento !== f.data ? { dataFaturamento: f.dataFaturamento } : {}),
    tabelaId: f.tabelaId,
    condicaoId: f.condicaoId,
    ...(f.cliente ? { clienteId: f.cliente.id } : {}),
    ...(f.ufDestino ? { ufDestino: f.ufDestino } : {}),
    ...(f.descricao.trim() ? { descricao: f.descricao.trim() } : {}),
    ...(fre.frete ? { frete: fre.frete } : {}),
    consideraFreteLucro: f.consideraFreteLucro,
    itens,
  };
  const r = orcamentoSchema.safeParse(candidato);
  return r.success ? { dto: r.data, problema: null } : { dto: null, problema: r.error.issues[0]?.message ?? 'Dados inválidos.' };
}

const norm = (t: string): string => decimalDigitado(t) ?? t.trim();

export type CampoForm = 'data' | 'tabela' | 'condição' | 'cliente' | 'UF de destino' | 'descrição' | 'frete' | 'considerar frete' | 'itens';

/** UF de destino depois de escolher um cliente: acompanha o endereco principal dele, salvo se o usuario ja escolheu outra UF. */
export function ufAoEscolherCliente(f: Pick<FormOrcamento, 'ufDestino' | 'ufDestinoManual'>, cliente: ClienteResumo | null): string {
  return f.ufDestinoManual ? f.ufDestino : (cliente?.uf ?? '');
}

/** Campos que diferem entre dois formularios (para "N campos alterados" e para saber se ha alteracao). */
export function camposAlterados(a: FormOrcamento, b: FormOrcamento): CampoForm[] {
  const out: CampoForm[] = [];
  if (a.data !== b.data || a.dataFaturamento !== b.dataFaturamento) out.push('data');
  if (a.tabelaId !== b.tabelaId) out.push('tabela');
  if (a.condicaoId !== b.condicaoId) out.push('condição');
  if ((a.cliente?.id ?? '') !== (b.cliente?.id ?? '')) out.push('cliente');
  if (a.ufDestino !== b.ufDestino) out.push('UF de destino');
  if (a.descricao.trim() !== b.descricao.trim()) out.push('descrição');
  const sf = (f: FormOrcamento) =>
    JSON.stringify([f.freteReceita, f.freteCusto, f.freteModo, f.freteTransportadora.trim(), f.freteBase, norm(f.freteTotal), norm(f.fretePorT), f.freteCreditaImpostos, f.freteDestacado, norm(f.freteCobrado)].map((v) => (typeof v === 'string' ? norm(v) : v)));
  if (sf(a) !== sf(b)) out.push('frete');
  if (a.consideraFreteLucro !== b.consideraFreteLucro) out.push('considerar frete');
  const sl = (f: FormOrcamento) => JSON.stringify(f.linhas.map((l) => [l.itemId, norm(l.quantidade), norm(l.preco)]));
  if (sl(a) !== sl(b)) out.push('itens');
  return out;
}

/** Data local de hoje (YYYY-MM-DD); so formata, nao calcula preco. */
export function hoje(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const ROTULO_STATUS: Record<OrcamentoResponse['status'], string> = {
  RASCUNHO: 'Rascunho',
  ENVIADO: 'Enviado',
  EM_NEGOCIACAO: 'Em negociação',
  APROVADO: 'Aprovado',
  PERDIDO: 'Perdido',
};

/** Categoria (opcional) do motivo de perda; os valores sao os do contrato. */
export const ROTULO_CATEGORIA: Record<NonNullable<OrcamentoResponse['motivoPerdidoCategoria']>, string> = {
  PRECO: 'Preço',
  PRAZO: 'Prazo',
  CONCORRENTE: 'Concorrente',
  DESISTIU: 'Desistiu',
  OUTRO: 'Outro',
};

/** Busca do detalhe: `?versao=N` (inteiro entre 1 e 9999) e `?guia=versoes` (aba Versoes). O resto e descartado. */
export function lerBuscaOrcamento(bruto: Record<string, unknown>): { versao?: number; guia?: 'versoes' } {
  const n = Number(bruto.versao);
  return {
    ...(Number.isInteger(n) && n >= 1 && n <= 9999 ? { versao: n } : {}),
    ...(bruto.guia === 'versoes' ? { guia: 'versoes' as const } : {}),
  };
}
